// src/layouts/ClientLayout.jsx
import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate, NavLink } from 'react-router-dom';
import { 
  FiFileText, 
  FiFile, 
  FiCreditCard, 
  FiCheckCircle, 
  FiLogOut, 
  FiUsers, 
  FiSlash, 
  FiMenu,
  FiX,
  FiHome
} from 'react-icons/fi';
import TaskToolbar from '../pages/admin-client/TaskToolbar';
import AddTaskForm from '../pages/admin-client/AddTaskForm';
import api from '../api';

const menuItems = [
  { icon: FiHome, label: 'Accueil', path: '/admin-client/home' },
  { icon: FiFileText, label: 'Ticket', path: '/admin-client/ticket' },
  { icon: FiCreditCard, label: 'Facture', path: '/admin-client/facture' },
  { icon: FiFile, label: 'Devis', path: '/admin-client/devis' },
  { icon: FiCheckCircle, label: 'Confirmation', path: '/admin-client/confirmation' },
  { icon: FiUsers, label: 'Utilisateurs', path: '/admin-client/employees' },
  { icon: FiSlash, label: 'Demandes Refusées', path: '/admin-client/demande-refuser' },
];

const secondaryItems = [
  { icon: FiLogOut, label: 'Déconnexion', path: '/' },
];

function NavItem({ Icon, label, path, onClick, isMobile, isActive }) {
  return (
    <li>
      <NavLink
        to={path}
        onClick={onClick}
        className={({ isActive }) => `
          flex items-center p-3 rounded-lg transition-all duration-200 group
          ${isActive 
            ? 'bg-[#875A7B] text-white shadow-sm' 
            : 'text-gray-600 hover:bg-gray-100 hover:text-[#875A7B]'
          }
          ${isMobile ? 'justify-start space-x-3' : 'justify-center'}
        `}
        title={isMobile ? '' : label}
      >
        <Icon className="w-5 h-5 flex-shrink-0" />
        {isMobile && <span className="text-sm font-medium">{label}</span>}
      </NavLink>
    </li>
  );
}

const AdminclientLayout = () => {
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [dateStart, setDateStart] = useState(null);
  const [dateEnd, setDateEnd] = useState(null);

  // Détection de la taille d'écran
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 1024;
      setIsMobile(mobile);
      if (!mobile) {
        setMobileMenuOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Empêcher le défilement du body quand le menu mobile est ouvert
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [mobileMenuOpen]);

  // Actions
  const handleShowForm = () => setShowForm(true);
  const handleSearch = value => setSearchTerm(value);
  const handleDateStart = value => setDateStart(value);
  const handleDateEnd = value => setDateEnd(value);
  const handleModuleChange = (module, path) => {
    navigate(path);
    if (isMobile) setMobileMenuOpen(false);
  };
const handleLogout = async () => {
  try {
    await api.post('/logout'); // Appel vers Laravel
    localStorage.clear();      // Nettoyer localStorage
    navigate('/');             // Rediriger vers login
  } catch (error) {
    console.error('Erreur logout :', error);
    alert('Impossible de se déconnecter.');
  }
};
  const toggleMobileMenu = () => setMobileMenuOpen(!mobileMenuOpen);

  return (
    <div className="h-screen bg-gray-50 flex flex-col">
      {/* Top Navigation Bar - Fixe et toujours visible */}
      <div className="fixed top-0 left-0 right-0 z-40 bg-white shadow-sm">
        <TaskToolbar
          onShowForm={handleShowForm}
          onSearch={handleSearch}
          onDateStartChange={handleDateStart}
          onDateEndChange={handleDateEnd}
          onLogout={handleLogout}
          onMenuToggle={toggleMobileMenu}
          isMobile={isMobile}
          mobileMenuOpen={mobileMenuOpen}
        />
      </div>

      {/* Layout Container avec marge pour la toolbar */}
      <div className="flex flex-1 pt-16 h-full">
        {/* Sidebar pour desktop */}
        {!isMobile && (
          <aside className="fixed left-0 top-16 bottom-0 w-20 bg-white border-r border-gray-200 flex flex-col shadow-sm z-30">
            {/* Navigation Menu */}
            <nav className="flex-1 p-3 overflow-y-auto">
              <ul className="space-y-2">
                {menuItems.map(item => (
                  <NavItem
                    key={item.label}
                    Icon={item.icon}
                    label={item.label}
                    path={item.path}
                    onClick={() => handleModuleChange(item.label, item.path)}
                    isMobile={false}
                  />
                ))}
              </ul>
            </nav>

            {/* Footer Section */}
            <footer className="p-3 border-t border-gray-200">
              <ul className="space-y-2">
                {secondaryItems.map(item => (
                  <NavItem
                    key={item.label}
                    Icon={item.icon}
                    label={item.label}
                    path={item.path}
                    onClick={handleLogout}
                    isMobile={false}
                  />
                ))}
              </ul>
            </footer>
          </aside>
        )}

        {/* Mobile menu overlay */}
        {isMobile && mobileMenuOpen && (
          <>
            <div 
              className="fixed inset-0 z-40 bg-black bg-opacity-50 transition-opacity duration-300"
              onClick={() => setMobileMenuOpen(false)}
            />
            <div 
              className="fixed left-0 top-16 bottom-0 w-64 bg-white shadow-lg transform transition-transform duration-300 z-50 overflow-y-auto"
              style={{ transform: mobileMenuOpen ? 'translateX(0)' : 'translateX(-100%)' }}
            >
              <div className="p-4 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-800">Navigation</h2>
              </div>
              
              <nav className="p-3">
                <ul className="space-y-1">
                  {menuItems.map(item => (
                    <NavItem
                      key={item.label}
                      Icon={item.icon}
                      label={item.label}
                      path={item.path}
                      onClick={() => handleModuleChange(item.label, item.path)}
                      isMobile={true}
                    />
                  ))}
                </ul>
              </nav>
              
              <footer className="p-3 border-t border-gray-200 mt-auto">
                <ul className="space-y-1">
                  {secondaryItems.map(item => (
                    <NavItem
                      key={item.label}
                      Icon={item.icon}
                      label={item.label}
                      path={item.path}
                      onClick={handleLogout}
                      isMobile={true}
                    />
                  ))}
                </ul>
              </footer>
            </div>
          </>
        )}

        {/* Main Content Area */}
        <main className={`flex-1 bg-gray-50 overflow-hidden h-full transition-all duration-300 ${
          isMobile ? 'w-full' : 'ml-20'
        }`}>
          {/* Add Task Form Modal */}
          {showForm && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                <AddTaskForm
                  onClose={() => setShowForm(false)}
                  onAddTask={(newTask) => {/* implement as needed */}}
                />
              </div>
            </div>
          )}

          {/* Content Outlet */}
          <div className="h-full overflow-auto p-4">
            <Outlet context={{ searchTerm, dateStart, dateEnd }} />
          </div>
        </main>
      </div>
    </div>
  );
};

export default AdminclientLayout;